import axios from 'axios';
import { message } from 'antd';

const api = axios.create({
  baseURL: '/api',
  // AI 模块（翻译/邮件分析等）为强制思考型模型，长文响应可达 2-3 分钟
  timeout: 300000,
});

// 请求拦截器 - 自动携带token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // 405 Method Not Allowed 多为路由方法错/参数传输形态异常，非用户操作错误；静默降级不弹 notification
    if (error.response?.status === 405) {
      return Promise.reject(error);
    }
    const msg = error.response?.data?.detail || error.message || '请求失败';
    message.error(msg);
    return Promise.reject(error);
  }
);

// ============ 认证 ============
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  csUsers: () => api.get('/auth/cs-users'),
  assignableUsers: () => api.get('/auth/assignable-users'),
};

// ============ 订单工单 ============
export const orderApi = {
  list: (params) => api.get('/orders', { params }),
  listAllTypes: (params) => api.get('/orders/all-types', { params }),
  get: (id) => api.get(`/orders/${id}`),
  lookupByNo: (orderNo) => api.get('/orders/lookup-by-no', { params: { order_no: orderNo } }),
  existsByNos: (nos) => api.get('/orders/exists-by-nos', {
    params: { order_no: nos },
    // 序列化为重复键 order_no=a&order_no=b（FastAPI list[str] 查询参数口径）
    paramsSerializer: (p) => (p.order_no || []).map((n) => `order_no=${encodeURIComponent(n)}`).join('&'),
  }),
  create: (data) => api.post('/orders', data),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/orders/upload-image', fd);
  },
  uploadFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/orders/upload-file', fd);
  },
  update: (id, data) => api.put(`/orders/${id}`, data),
  recordAction: (id, data) => api.post(`/orders/${id}/action`, data),
  complete: (id) => api.post(`/orders/${id}/complete`),
  reopen: (id, payload) => api.post(`/orders/${id}/reopen`, payload || {}),
  backToPending: (id, data) => api.post(`/orders/${id}/back-to-pending`, data),
  toAwaitingCustomer: (id, payload) => api.post(`/orders/${id}/to-awaiting-customer`, typeof payload === 'string' ? { result_note: payload } : payload),
  customerReply: (id, data) => api.post(`/orders/${id}/customer-reply`, data),
  customerRead: (id) => api.post(`/orders/${id}/customer-read`),
  import: (data) => api.post('/orders/import', data),
  getLogs: (id) => api.get(`/orders/${id}/logs`),
  getReminders: () => api.get('/orders/reminders/list'),
  getReminderCount: () => api.get('/orders/reminders/count'),
  markReminderRead: (id) => api.post(`/orders/reminders/${id}/read`),
  markAllRead: () => api.post('/orders/reminders/read-all'),
  getPopupReminder: () => api.get('/orders/reminders/popup'),
  resetPopupQueue: () => api.post('/orders/reminders/popup/reset'),
  ackPopupReminder: (id, action) => api.post('/orders/reminders/popup/ack', { reminder_id: id, action }),
  snoozePopup: (mode, reminderId) => api.post('/orders/reminders/popup/snooze', { mode, reminder_id: reminderId }),
  escalate: (id) => api.post(`/orders/${id}/escalate`),
  dismissEscalation: (id) => api.post(`/orders/${id}/escalation-dismiss`),
};

// ============ 待识别邮件 ============
export const emailApi = {
  listUnidentified: (params) => api.get('/emails/unidentified', { params }),
  unreadCount: () => api.get('/emails/unread-count'),
  get: (id) => api.get(`/emails/${id}`),
  sync: () => api.post('/emails/sync'),
  markRead: (id) => api.post(`/emails/${id}/read`),
  batchRead: (ids) => api.post('/emails/batch-read', { ids }),
  matchOrder: (id, orderId) => api.post(`/emails/${id}/match`, { order_id: orderId }),
  matchEmail: (id, payload) => api.post(`/emails/${id}/match`, payload),
  translate: (id, force = false) => api.post(`/emails/${id}/translate`, null, { params: { force } }),
};

// ============ 客户管理 ============
export const customerApi = {
  list: (params) => api.get('/customers', { params }),
  listNames: () => api.get('/customers/names'),
  create: (data) => api.post('/customers', data),
  updateLevel: (id, level) => api.patch(`/customers/${id}`, { level }),
};

// ============ 优先级规则 ============
export const priorityApi = {
  getDimensions: () => api.get('/priority/dimensions'),
  evaluate: (optionIds) => api.post('/priority/evaluate', { option_ids: optionIds }),
  createDimension: (data) => api.post('/priority/dimensions', data),
  renameDimension: (id, data) => api.put(`/priority/dimensions/${id}`, data),
  deleteDimension: (id) => api.delete(`/priority/dimensions/${id}`),
  addOption: (dimensionId, data) => api.post(`/priority/dimensions/${dimensionId}/options`, data),
  updateOption: (id, data) => api.put(`/priority/options/${id}`, data),
  deleteOption: (id) => api.delete(`/priority/options/${id}`),
  updatePolicy: (id, data) => api.put(`/priority/policies/${id}`, data),
};

// ============ 策略分析 ============
export const strategyApi = {
  createThread: (data) => api.post('/strategy/threads', data),
  batchImportThreads: (data) => api.post('/strategy/threads/batch-import', data),
  listThreads: (params) => api.get('/strategy/threads', { params }),
  analyzeThread: (id) => api.post(`/strategy/threads/${id}/analyze`),
  listCases: (params) => api.get('/strategy/cases', { params }),
  getCase: (id) => api.get(`/strategy/cases/${id}`),
  createCase: (data) => api.post('/strategy/cases', data),
  updateCase: (id, data) => api.put(`/strategy/cases/${id}`, data),
  deleteCase: (id) => api.delete(`/strategy/cases/${id}`),
  query: (data) => api.post('/strategy/query', data),
  feedback: (data) => api.post('/strategy/feedback', data),
  feedbackStats: () => api.get('/strategy/feedback/stats'),
  problemTypes: () => api.get('/strategy/problem-types'),
  // 审核与有效程度（admin）
  adminListCases: (params) => api.get('/strategy/admin/cases', { params }),
  approveCase: (id) => api.post(`/strategy/cases/${id}/approve`),
  rejectCase: (id, reject_reason) => api.post(`/strategy/cases/${id}/reject`, { reject_reason }),
  setEffectiveness: (id, effectiveness) => api.post(`/strategy/cases/${id}/effectiveness`, { effectiveness }),
  archiveCase: (id) => api.post(`/strategy/cases/${id}/archive`),
};

// ============ 翻译 ============
export const translationApi = {
  translate: (data) => api.post('/translation/translate', data),
  listTerminology: (params) => api.get('/translation/terminology', { params }),
  createTerminology: (data) => api.post('/translation/terminology', data),
  updateTerminology: (id, data) => api.put(`/translation/terminology/${id}`, data),
  deleteTerminology: (id) => api.delete(`/translation/terminology/${id}`),
  getCategories: () => api.get('/translation/terminology/categories'),
  getHistory: (params) => api.get('/translation/history', { params }),
};

// ============ 科维斯订单 ============
export const kewellApi = {
  // 按 查询编码/PRO号/BOL号/订单编号 精确查询（后端代理科维斯 token + 查询）
  queryOrder: (keyword) => api.get('/kewell/orders/query', { params: { keyword } }),
};

// ============ 报价策略 ============
export const pricingApi = {
  listRules: (params) => api.get('/pricing/rules', { params }),
  createRule: (data) => api.post('/pricing/rules', data),
  updateRule: (id, data) => api.put(`/pricing/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/pricing/rules/${id}`),
  getConditions: () => api.get('/pricing/conditions'),
  addCondition: (data) => api.post('/pricing/conditions', null, { params: data }),
  analyze: (data) => api.post('/pricing/analyze', data),
  aiSuggest: (data) => api.post('/pricing/ai-suggest', data),
};

// ============ 账单申诉 ============
export const appealApi = {
  list: (params) => api.get('/appeals', { params }),
  summary: () => api.get('/appeals/summary'),
  get: (id) => api.get(`/appeals/${id}`),
  create: (data) => api.post('/appeals', data),
  update: (id, data) => api.put(`/appeals/${id}`, data),
  addChat: (id, content) => api.post(`/appeals/${id}/chat`, { content }),
  changeStatus: (id, status) => api.post(`/appeals/${id}/status`, { status }),
  complete: (id) => api.post(`/appeals/${id}/complete`),
  reopen: (id, payload) => api.post(`/appeals/${id}/reopen`, payload || {}),
  backToPending: (id, data) => api.post(`/appeals/${id}/back-to-pending`, data),
  toAwaitingCustomer: (id, payload) => api.post(`/appeals/${id}/to-awaiting-customer`, payload || {}),
  customerReply: (id, data) => api.post(`/appeals/${id}/customer-reply`, data),
  customerRead: (id) => api.post(`/appeals/${id}/customer-read`),
  updateCustomer: (id, customer_name) => api.patch(`/appeals/${id}/customer`, { customer_name }),
  getLogs: (id) => api.get(`/appeals/${id}/logs`),
  uploadFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/appeals/upload-file', fd);
  },
};

// ============ 工单闹钟（企微提醒；三大工单模块共用） ============
export const alarmApi = {
  create: (data) => api.post('/alarms', data),
  list: (orderType, orderId) => api.get('/alarms', { params: { order_type: orderType, order_id: orderId } }),
  mine: (orderType) => api.get('/alarms/mine', { params: { order_type: orderType } }),
  cancel: (id) => api.delete(`/alarms/${id}`),
};

// ============ 货物索赔 ============
export const claimApi = {
  list: (params) => api.get('/claims', { params }),
  summary: () => api.get('/claims/summary'),
  get: (id) => api.get(`/claims/${id}`),
  create: (data) => api.post('/claims', data),
  update: (id, data) => api.put(`/claims/${id}`, data),
  addChat: (id, content) => api.post(`/claims/${id}/chat`, { content }),
  changeStatus: (id, status) => api.post(`/claims/${id}/status`, { status }),
  complete: (id) => api.post(`/claims/${id}/complete`),
  reopen: (id, payload) => api.post(`/claims/${id}/reopen`, payload || {}),
  backToPending: (id, data) => api.post(`/claims/${id}/back-to-pending`, data),
  toAwaitingCustomer: (id, payload) => api.post(`/claims/${id}/to-awaiting-customer`, payload || {}),
  customerReply: (id, data) => api.post(`/claims/${id}/customer-reply`, data),
  customerRead: (id) => api.post(`/claims/${id}/customer-read`),
  getLogs: (id) => api.get(`/claims/${id}/logs`),
  uploadFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/claims/upload-file', fd);
  },
};

export default api;

// ============ 返账单登记 ============
export const rebillApi = {
  list: (params) => api.get('/rebills', { params }),
  summary: () => api.get('/rebills/summary'),
  get: (id) => api.get(`/rebills/${id}`),
  create: (data) => api.post('/rebills', data),
  update: (id, data) => api.put(`/rebills/${id}`, data),
  toCs: (id, payload) => api.post(`/rebills/${id}/to-cs`, payload || {}),
  csReply: (id, data) => api.post(`/rebills/${id}/cs-reply`, data),
  close: (id, data) => api.post(`/rebills/${id}/close`, data || {}),
  reopen: (id, payload) => api.post(`/rebills/${id}/reopen`, payload || {}),
  getLogs: (id) => api.get(`/rebills/${id}/logs`),
  byBol: (bol) => api.get('/rebills/by-bol', { params: { bol } }),
  linkAppeal: (id, appealId) => api.post(`/rebills/${id}/links/${appealId}`),
  unlinkAppeal: (id, appealId) => api.delete(`/rebills/${id}/links/${appealId}`),
  exportExcel: (params) => api.get('/rebills/export/excel', { params, responseType: 'blob' }),
  uploadFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/rebills/upload-file', fd);
  },
  // 提醒（独立于订单提醒，每用户独立未读计数）
  getReminders: (params) => api.get('/rebills/reminders', { params }),
  getReminderCount: () => api.get('/rebills/reminders/count'),
  markReminderRead: (id) => api.post(`/rebills/reminders/${id}/read`),
  markAllRemindersRead: () => api.post('/rebills/reminders/read-all'),
};
